import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { CreatePlanButton } from "./-components/create-plan-button"
import { PlanTable } from "./-components/plan-table"
import { requirePlanPages } from "./-lib/gate"
import {
  type PlansSearch,
  plansSearchSchema,
  TABS,
  tabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/planning")({
  staticData: { breadcrumb: "Planning" },
  validateSearch: (search) => plansSearchSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) => requirePlanPages(queryClient),
  component: PlansPage,
})

function useUpdateSearch(): (patch: Partial<PlansSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function PlansPage(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <ListPage toolbar={<Toolbar />} detail={<Outlet />}>
      <PlanTable
        search={search}
        onClear={() => update({ search: undefined })}
      />
    </ListPage>
  )
}

function Toolbar(): React.ReactElement {
  const { tab = "all", search = "" } = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({ tab: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search plans…"
          />
          <CreatePlanButton />
        </>
      }
    />
  )
}
